import { SEARCH_TYPES, type SearchType } from '@/api/search';

/** How many records the search remembers; the oldest falls off the end. */
export const MAX_RECENT_RECORDS = 8;

const STORAGE_PREFIX = 'fieldops.search.recent.';

/** A record opened from search, kept so that it is one keystroke away next time. */
export interface RecentRecord {
  readonly type: SearchType;
  readonly id: string;
  readonly title: string;
  /** What said more about it when it was found: a number, an address, a date. */
  readonly detail: string | null;
}

function isSearchType(value: unknown): value is SearchType {
  return typeof value === 'string' && (SEARCH_TYPES as readonly string[]).includes(value);
}

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function textOf(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** Reads one remembered record, or null when what was stored is not one. */
function readRecent(raw: unknown): RecentRecord | null {
  if (!isRecord(raw)) {
    return null;
  }
  const id = textOf(raw.id);
  const title = textOf(raw.title);
  if (!isSearchType(raw.type) || id === null || title === null) {
    return null;
  }
  return { type: raw.type, id, title, detail: textOf(raw.detail) };
}

/**
 * The records this browser remembers for a person, newest first.
 *
 * Kept per principal, so that one person's recent customers are never offered to the next person
 * to sign in on the same machine. Storage can be missing, full or blocked, and what it holds can
 * be anything, so every read answers "nothing remembered" rather than failing.
 *
 * @param identity who is signed in; nobody is remembered for no one
 * @returns up to {@link MAX_RECENT_RECORDS} records
 */
export function readRecentRecords(identity: string | null): readonly RecentRecord[] {
  if (identity === null) {
    return [];
  }
  try {
    const stored = globalThis.localStorage.getItem(STORAGE_PREFIX + identity);
    const parsed: unknown = stored === null ? [] : JSON.parse(stored);
    return Array.isArray(parsed)
      ? parsed
          .map(readRecent)
          .filter((record) => record !== null)
          .slice(0, MAX_RECENT_RECORDS)
      : [];
  } catch {
    return [];
  }
}

/**
 * Puts a record at the front of what is remembered, once.
 *
 * @param identity who is signed in
 * @param record the record that was opened
 */
export function rememberRecentRecord(identity: string | null, record: RecentRecord): void {
  if (identity === null) {
    return;
  }
  const others = readRecentRecords(identity).filter(
    (kept) => kept.type !== record.type || kept.id !== record.id,
  );
  try {
    globalThis.localStorage.setItem(
      STORAGE_PREFIX + identity,
      JSON.stringify([record, ...others].slice(0, MAX_RECENT_RECORDS)),
    );
  } catch {
    // Storage may be full or blocked; search works the same without a memory.
  }
}
